import { BriefcaseIcon, LightbulbIcon, PencilIcon, ShoppingBagIcon, StoreIcon } from '@air/design-icons';

import { type WorkspaceAgentIconComponent } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/types';
import { type WorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentStructuredDisplay';

export type WorkspaceAgentBoardTemplateOption = {
  icon: WorkspaceAgentIconComponent;
  iconName: string;
  id: string;
  /** `describe` waits for the user to finish the sentence; every other option sends on click. */
  kind: 'describe' | 'send';
  /** Builds the seeded prompt; the board is cited as an `@[board:<id>]` moniker that the composer renders as its pill. */
  getPrompt: (boardId: string) => string;
  title: string;
};

const toBoardMoniker = (boardId: string) => `@[board:${boardId}]`;

const PLAN_CONSTRAINTS =
  'Use the current year in any dated titles. Keep it to at most 70 boards and 3 levels. Show me the plan, and create the boards after I approve.';

const buildIndustryPrompt = (industry: string) => (boardId: string) =>
  `Create a ${industry} sub-board structure inside ${toBoardMoniker(boardId)}. ${PLAN_CONSTRAINTS}`;

export const WORKSPACE_AGENT_BOARD_TEMPLATE_OPTIONS = [
  {
    icon: BriefcaseIcon,
    iconName: 'briefcase',
    id: 'board-template-agency',
    kind: 'send',
    getPrompt: buildIndustryPrompt('Agency'),
    title: 'Agency template',
  },
  {
    icon: StoreIcon,
    iconName: 'store',
    id: 'board-template-retail',
    kind: 'send',
    getPrompt: buildIndustryPrompt('Retail'),
    title: 'Retail template',
  },
  {
    icon: ShoppingBagIcon,
    iconName: 'shopping-bag',
    id: 'board-template-ecommerce',
    kind: 'send',
    getPrompt: buildIndustryPrompt('E-commerce'),
    title: 'E-commerce template',
  },
  {
    icon: LightbulbIcon,
    iconName: 'lightbulb',
    id: 'board-template-recommend',
    kind: 'send',
    getPrompt: (boardId) =>
      `Look at what is already in ${toBoardMoniker(boardId)} and recommend a sub-board structure that fits it. ${PLAN_CONSTRAINTS}`,
    title: 'Recommend a structure',
  },
  {
    icon: PencilIcon,
    iconName: 'pencil',
    id: 'board-template-describe',
    kind: 'describe',
    getPrompt: (boardId) => `Create a sub-board structure inside ${toBoardMoniker(boardId)} for: `,
    title: "I'll describe what it's for",
  },
] satisfies readonly WorkspaceAgentBoardTemplateOption[];

export const getWorkspaceAgentBoardTemplateOptionById = (id: string): WorkspaceAgentBoardTemplateOption | undefined =>
  WORKSPACE_AGENT_BOARD_TEMPLATE_OPTIONS.find((option) => option.id === id);

export const getWorkspaceAgentBoardTemplateStructuredDisplay = ({
  boardTitle,
  option,
}: {
  boardTitle: string;
  option: WorkspaceAgentBoardTemplateOption;
}): WorkspaceAgentStructuredDisplay => ({
  description: `Sub-boards inside ${boardTitle}`,
  icon: option.iconName,
  title: option.title,
});
